import { useTranslation } from 'react-i18next';
import { styled } from 'goober';

import { Button } from '@/ui/button/button';
import { Stack } from '@/ui/line/line';

import { Card } from './claude-mcp-connector.styles';
import {
  ClaudeMcpConnectorHeader,
  EnvironmentBadge,
} from './claude-mcp-connector-header';
import type { AccountScopedMcpConnectorConfig } from '../mcp-oauth/mcp-oauth-connector-config';
import type { SupportedMcpOAuthEnvironment } from '../mcp-oauth/mcp-oauth-environment';
import { McpOAuthSetupTabs } from '../mcp-oauth/mcp-oauth-setup-tabs';
import { SectionDivider } from '../mcp-oauth/mcp-oauth.styles';

interface ClaudeMcpConnectorNotConnectedViewProps {
  connectorConfig: AccountScopedMcpConnectorConfig;
  copiedFieldId: string | null;
  displayEnvironment: SupportedMcpOAuthEnvironment;
  isConnecting: boolean;
  isEnvironmentSupported: boolean;
  onConnect: () => void;
  onCopy: (value: string, fieldId: string) => void;
  showAdvancedSettingsHint: boolean;
}

export const ClaudeMcpConnectorNotConnectedView = ({
  connectorConfig,
  copiedFieldId,
  displayEnvironment,
  isConnecting,
  isEnvironmentSupported,
  onConnect,
  onCopy,
  showAdvancedSettingsHint,
}: ClaudeMcpConnectorNotConnectedViewProps) => {
  const { t } = useTranslation('settings', { keyPrefix: 'accessTokensTab' });

  const renderEnvironmentBadge = () => {
    if (!isEnvironmentSupported) {
      return (
        <EnvironmentBadge>{t`mcpOAuth.environmentBadgeLocalPreview`}</EnvironmentBadge>
      );
    }

    if (displayEnvironment.kind === 'staging') {
      return (
        <EnvironmentBadge>{t`mcpOAuth.environmentStaging`}</EnvironmentBadge>
      );
    }

    return null;
  };

  const environmentBadge = renderEnvironmentBadge();

  return (
    <Card>
      <Stack fullWidth gap="14px">
        <ClaudeMcpConnectorHeader
          subtitle={t`mcpOAuth.cardSubtitle`}
          title={t`mcpOAuth.cardTitle`}
          titleExtras={environmentBadge}
        />

        <McpOAuthSetupTabs
          connectorConfig={connectorConfig}
          copiedFieldId={copiedFieldId}
          onCopy={onCopy}
          showAdvancedSettingsHint={showAdvancedSettingsHint}
          showStatusFooter
        />

        <SectionDivider />
        <SetupHint>{t`mcpOAuth.notConnectedDescription`}</SetupHint>
        <Button
          disabled={isConnecting}
          onClick={onConnect}
          type="button"
          variant="primary"
        >
          {isConnecting ? t`mcpOAuth.connecting` : t`mcpOAuth.connect`}
        </Button>
      </Stack>
    </Card>
  );
};

const SetupHint = styled('p')`
  color: ${({ theme }) => theme.colors.gray.c12};
  font-family: ${({ theme }) => theme.fonts.neue};
  font-size: 13px;
  line-height: 1.5;
  margin: 0;
`;
